import React from 'react'
import { Card } from 'primereact/card'
import { Tooltip } from 'primereact/tooltip'
import { useOrderData } from '../hooks/useOrderData'
import { useOrderTabs } from '../hooks/useOrderTabs'

type OrderDataType = ReturnType<typeof useOrderData>

interface DashboardMetricsProps {
  orderData: OrderDataType
  orderTabs: ReturnType<typeof useOrderTabs>
}

export const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ 
  orderData,
  orderTabs
}) => {
  // Calculate tab-based counts
  const tabCounts = React.useMemo(() => {
    return orderTabs.getTabCounts(orderData.orders)
  }, [orderData.orders, orderTabs])

  const handleMetricClick = (tabKey: string) => {
    orderTabs.setActiveTab(tabKey)
  }

  return (
    <div className="row mt-4 g-3" data-testid="dashboard-metrics-container">
      {/* Items Needing Assignment Card */}
      <div className="col-12 col-lg-3 d-flex">
        <Card 
          className="stats-card mb-0 w-100 cursor-pointer hover-shadow" 
          onClick={() => handleMetricClick('needs_assignment')}
          style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
          data-testid="metric-card-needs-assignment"
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-500 font-medium mb-2">Items Needing Assignment</div>
              <div className="d-flex align-items-center">
                {orderData.loading ? (
                  <div className="placeholder-wave w-100">
                    <span className="placeholder col-8 bg-primary"></span>
                  </div>
                ) : (
                  <>
                    <span className={`text-900 font-medium text-xl ${tabCounts.needs_assignment > 10 ? 'text-danger' : 'text-success'}`}>
                      {tabCounts.needs_assignment}
                    </span>
                    <div className="ms-2 mini-progress" 
                      style={{
                        backgroundColor: tabCounts.needs_assignment > 10 ? 'var(--red-100)' : 'var(--green-100)'
                      }}>
                      <div style={{
                        width: `${Math.min((tabCounts.needs_assignment / orderData.orders.length) * 100, 100)}%`,
                        height: '100%',
                        backgroundColor: tabCounts.needs_assignment > 10 ? 'var(--red-500)' : 'var(--green-500)'
                      }}/>
                    </div>
                  </>
                )}
              </div>
            </div>
            <i className={`pi pi-exclamation-circle text-xl ${tabCounts.needs_assignment > 10 ? 'text-danger' : 'text-success'}`}
                data-pr-tooltip="Orders that haven't been assigned to a fulfillment location yet. Click to view."
                data-pr-position="left"
            ></i>
            <Tooltip target="i" />
          </div>
        </Card>
      </div>

      {/* Awaiting Inspection Card */}
      <div className="col-12 col-lg-3 d-flex">
        <Card 
          className="stats-card mb-0 w-100 cursor-pointer hover-shadow"
          onClick={() => handleMetricClick('pending_review')}
          style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
          data-testid="metric-card-pending-review"
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-500 font-medium mb-2">Awaiting Inspection</div>
              <div className="d-flex align-items-center">
                {orderData.loading ? (
                  <div className="placeholder-wave w-100">
                    <span className="placeholder col-8 bg-primary"></span>
                  </div>
                ) : (
                  <>
                    <span className="text-900 font-medium text-xl text-warning">{tabCounts.pending_review}</span>
                    <i className="pi pi-eye text-warning ms-2"></i>
                  </>
                )}
              </div>
            </div>
            <i className="pi pi-search text-warning text-xl"
                data-pr-tooltip="Items waiting for quality inspection at fulfillment locations. Click to view."
                data-pr-position="left"
            ></i>
            <Tooltip target="i" />
          </div>
        </Card>
      </div>

      {/* Orders In Progress Card */}
      <div className="col-12 col-lg-3 d-flex">
        <Card 
          className="stats-card mb-0 w-100 cursor-pointer hover-shadow"
          onClick={() => handleMetricClick('active_orders')}
          style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
          data-testid="metric-card-active-orders"
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-500 font-medium mb-2">Orders In Progress</div>
              <div className="d-flex align-items-center">
                {orderData.loading ? (
                  <div className="placeholder-wave w-100">
                    <span className="placeholder col-8 bg-primary"></span>
                  </div>
                ) : (
                  <>
                    <span className="text-900 font-medium text-xl text-info">{tabCounts.active_orders}</span>
                    <i className="pi pi-cog text-info ms-2"></i>
                  </>
                )}
              </div>
            </div>
            <i className="pi pi-clock text-info text-xl"
                data-pr-tooltip="Orders that are fully assigned and currently being processed by fulfillment locations. Click to view."
                data-pr-position="left"
            ></i>
            <Tooltip target="i" />
          </div>
        </Card>
      </div>

      {/* Items Declined Card */}
      <div className="col-12 col-lg-3 d-flex">
        <Card 
          className="stats-card mb-0 w-100 cursor-pointer hover-shadow"
          onClick={() => handleMetricClick('declined')}
          style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
          data-testid="metric-card-declined"
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-500 font-medium mb-2">Items Declined</div>
              <div className="d-flex align-items-center">
                {orderData.loading ? (
                  <div className="placeholder-wave w-100">
                    <span className="placeholder col-8 bg-primary"></span>
                  </div>
                ) : (
                  <>
                    <span className="text-900 font-medium text-xl text-danger">{tabCounts.declined}</span>
                    <i className="pi pi-times-circle text-danger ms-2"></i>
                  </>
                )}
              </div>
            </div>
            <i className="pi pi-ban text-danger text-xl"
                data-pr-tooltip="Items declined by fulfillment locations that need reassignment. Click to view."
                data-pr-position="left"
            ></i>
            <Tooltip target="i" />
          </div>
        </Card>
      </div>
    </div>
  )
}